/* @refresh reload */
import { useApolloClient } from "@apollo/client/react";
import _ from "lodash";
import { useContext, type ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckIcon } from "lucide-react";
import {
  DocumentEnum,
  InvoiceStateEnum,
  TriageSuggestionStatusEnum,
  type DocumentFragment,
  DocumentFragmentDoc,
  type TriageDraftMatchesQuery,
  type TriageEmailConnectionSearchResultsQuery,
} from "@/__generated__/graphql";
import {
  SelectableCardList,
  type SelectableCardListGroup,
  type SelectableCardListLoadMore,
  type SelectableCardListSelectionMode,
} from "@/components/molecules/SelectableCardList";
import { SimpleEnum } from "@/components/molecules/SimpleEnum";
import { Icon } from "@/components/atoms/Icon";
import {
  SelectableListItemRow,
  SelectableListItemRowDelta,
  SelectableListItemRowTitleEnd,
  SelectableListItemRowTitleEndEmphasis,
  SELECTABLE_LIST_ITEM_ROW_TRIAGE_OPTION_HEIGHT,
  getSelectableListItemRowHeight,
  type SelectableListItemRowEnd,
} from "@/components/molecules/SelectableListItemRow";
import { Mutations } from "@/constants";
import { EnumColors } from "@/constants/values";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import { DocumentObject } from "@/objects/DocumentObject";
import { icons } from "@/objects/@icons";
import { State } from "@/state";
import { Utils } from "@/utils";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";
import { TriageSuggestionCard } from "./TriageChangelistProposalRow";

import { useTriageNoDocumentStop } from "./TriageNoDocumentOptions";
import {
  TriageSuggestionIndicatorPill,
  type SuggestionIndicatorSummary,
} from "./TriageSuggestionIndicators";
import {
  commitActiveOption,
  toggleLinesSuggestion,
  TriageDetailContext,
  useTriageDetailContext,
  type TriageDetail,
  type TriageSuggestionStub,
} from "./TriageView.context";
import { TriageRowHeaderLink, categoryEnumColor } from "./TriageReconcileRow";
import {
  TRIAGE_TAB_GROUP_FILTERS,
  type TriageResolutionTab,
} from "./TriageView.resolution";
import {
  buildTriageJournalEntryLines,
  TriageJournalEntryTable,
  type TriageJournalLineSource,
  type TriageJournalResolved,
  type TriageJournalSource,
} from "./TriageJournalEntryPanel";
import { TriageSettlementPreview } from "./TriageSettlement";
import { Checkbox } from "@/components/ui/checkbox";

/**
 * Match panel shell: the scrollable list. Its search and tabs are the content
 * area's toolbar, and its actions the suggestions' bar - both there whatever
 * the panel shows.
 */
type TriageMatchPanelShellProps = {
  scrollable?: React.ReactNode;
  /** The payment is loading: one spinner in the middle of the panel. */
  isLoading?: boolean;
};

export function TriageMatchPanelShell({
  scrollable: scrollableProp,
  isLoading = false,
}: TriageMatchPanelShellProps) {
  const scrollable = isLoading ? (
    // One spinner in the middle of the panel, in the cards' place - as the
    // suggestions show while they load: asked for here over the skeleton rows
    // the rest of the app draws.
    <div
      data-testid="triage-match-loading"
      className="h-full min-h-0 w-full flex items-center justify-center"
    >
      <Spinner className="size-8" />
    </div>
  ) : (
    scrollableProp
  );
  return (
    // The triage view's second content area, not a card laid on it: what it
    // holds starts at the area's own edges, its search row the bar across
    // the top.
    <div
      // The content surface: the whole match column is read off the canvas
      // the rest of the app reads its lists off, not the node panel's ground.
      className="h-full min-h-0 min-w-0 flex-1 flex flex-col overflow-hidden bg-muted dark:bg-[#151719]"
    >
      <div className="flex-1 min-h-0 min-w-0 overflow-hidden flex flex-col">
        {scrollable}
      </div>
    </div>
  );
}

/**
 * About how tall a row of each tab is, for the list to lay out before it has
 * measured one: each row is its content's height.
 */
const OPTION_ROW_HEIGHTS: Record<TriageResolutionTab, number> = {
  suggestions: SELECTABLE_LIST_ITEM_ROW_TRIAGE_OPTION_HEIGHT,
  document: getSelectableListItemRowHeight(1, { overline: true }),
  categorize: getSelectableListItemRowHeight(1),
  transactions: getSelectableListItemRowHeight(0, { overline: true }),
};

const TRIAGE_OPTION_LIST_SKELETON_COUNT = 6;

/**
 * The payment a placeholder suggestion is about while none is loaded to say
 * it against: enough of one for a suggestion's header and its sentence.
 */
export const TRIAGE_PLACEHOLDER = {
  id: "placeholder",
  identifier: "placeholder",
  description: "Northwind Supplies",
  amount: "-1250.00",
  effectiveDate: "2026-01-15",
  entity: {
    id: "placeholder-entity",
    identifier: "placeholder-entity",
    name: "Northwind Traders",
  },
  financialAccount: {
    id: "placeholder-account",
    identifier: "placeholder-account",
    name: "Operating account",
    chartedAccount: {
      id: "placeholder-bank",
      identifier: "placeholder-bank",
      name: "Operating account",
    },
  },
  suggestions: [],
} as unknown as TriageDetail;

/** The panel's search box, where the suggestions are stepped through from: found as it is asked for, being drawn by the view, not the list. */
const SUGGESTIONS_SEARCH = {
  get current() {
    return document.querySelector<HTMLElement>(
      "[data-testid='triage-suggestions-search']",
    );
  },
};

/**
 * What a tab's rows are while it loads: a typical row of that tab's, drawn as
 * any of them is - so a loading row is the size of a loaded one.
 */
export const TRIAGE_OPTION_PLACEHOLDERS: Record<
  TriageResolutionTab,
  TriageOptionListItem
> = {
  suggestions: {
    key: "lines:placeholder",
    sectionId: "lines",
    selectionMode: "single",
    title: "Software subscriptions",
    confidence: 0.9,
    suggestion: {
      identifier: "placeholder",
      job: "CATEGORIZE",
      confidence: 0.9,
      rationale: "Categorize as software subscriptions.",
      lines: [
        {
          identifier: "placeholder-bank-line",
          amount: "-1250.00",
          description: "Bank",
          chartedAccount: {
            identifier: "placeholder-bank",
            name: "Operating account",
          },
        },
        {
          identifier: "placeholder-offset-line",
          amount: "1250.00",
          description: "Software",
          chartedAccount: {
            identifier: "placeholder-offset",
            name: "Software subscriptions",
          },
        },
      ],
    } as unknown as TriageSuggestionStub,
    proposalGrouped: true,
    selected: false,
  },
  document: {
    key: "document:placeholder",
    sectionId: "documents",
    selectionMode: "multi",
    title: "Invoice INV-2041 — Northwind Supplies",
    documentType: DocumentEnum.Invoice,
    invoiceState: InvoiceStateEnum.Sent,
    contact: {
      __typename: "ContactObject",
      id: "placeholder-contact",
      identifier: "placeholder-contact",
      name: "Northwind Supplies",
    },
    breadcrumb: [{ nodeId: null, label: "Northwind Traders" }],
    dateLabel: "Jan 13, 26",
    dateDeltaDays: -2,
    dateDeltaLabel: "−2d",
    amountLabel: "$1,250.00",
    amountDelta: 0,
    panelNodeId: "placeholder-document",
    selected: false,
  },
  categorize: {
    key: "account:placeholder",
    sectionId: "accounts",
    selectionMode: "single",
    title: "Expenses / Software subscriptions",
    categories: [
      {
        id: "placeholder-category",
        identifier: "placeholder-category",
        name: "Software",
        color: "BLUE",
      },
    ],
    selected: false,
  },
  transactions: {
    key: "transaction:placeholder",
    sectionId: "transactions",
    selectionMode: "multi",
    title: "Northwind Supplies",
    breadcrumb: [
      { nodeId: null, label: "Northwind Traders" },
      { nodeId: null, label: "Operating account" },
    ],
    dateLabel: "Jan 13, 26",
    dateDeltaDays: -2,
    dateDeltaLabel: "−2d",
    amountLabel: "$1,250.00",
    amountDelta: 0,
    panelNodeId: "placeholder-transaction",
    selected: false,
  },
};

/**
 * The entry the payment would book, looked over before it is booked: under
 * the suggestions' bar while its Preview there is pressed (or its key), shut
 * with no payment. A suggestion picked that books in another business as well
 * shows both businesses' entries, as the person says they settle it.
 */
export function TriagePreview({
  triage,
  resolved,
  isLoading = false,
  onRemoveLineSource,
}: {
  triage: TriageJournalSource;
  resolved: TriageJournalResolved;
  isLoading?: boolean;
  onRemoveLineSource?: (source: TriageJournalLineSource) => void;
}) {
  const detail = useContext(TriageDetailContext);
  const isOpen = detail?.isPreviewOpen ?? false;
  const picked = detail?.resolved.linesSuggestion ?? null;
  const settlesElsewhere =
    picked?.lines?.some(
      (line) => line.entity?.identifier !== triage.entity?.identifier,
    ) ?? false;

  return (
    <AnimatePresence initial={false}>
      {isOpen ? (
        <motion.div
          key="triage-preview"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
          style={{ overflow: "hidden", flexShrink: 0 }}
          onClick={(event) => event.stopPropagation()}
        >
          {/* The width of the panel, with nothing drawn around it but the
              rule under it, between it and the suggestions: the panel is the
              page's right-hand side rather than a card, and a card inside it
              for the entry was one frame too many. */}
          <div
            data-testid="triage-match-journal-entry"
            className="min-w-0 max-w-full overflow-hidden [border-bottom-width:1px] [border-bottom-style:solid] border-border"
          >
            {picked && settlesElsewhere && triage.entity ? (
              <TriageSettlementPreview
                suggestionId={picked.id}
                payerIdentifier={triage.entity.identifier}
                treatment={detail?.triage?.settlementTreatment ?? null}
              />
            ) : (
              <TriageJournalEntryTable
                triage={triage}
                resolved={resolved}
                // A draft's, in review: the entry it would book once
                // accepted - looked over, not changed.
                lines={
                  resolved.linesSuggestion?.status === "IN_REVIEW"
                    ? buildTriageJournalEntryLines(triage, resolved)
                    : undefined
                }
                isLoading={isLoading}
                onRemoveLineSource={onRemoveLineSource}
                settlement={detail?.journalSettlement ?? null}
              />
            )}
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

export type TriageOptionListItem = {
  key: string;
  sectionId: string;
  selectionMode: SelectableCardListSelectionMode;
  title: string;
  description?: string;
  /** Document rows: type / state enums + optional subtitle under the title. */
  documentType?: DocumentEnum | null;
  invoiceState?: InvoiceStateEnum | null;
  isOverdue?: boolean;
  subtitle?: string;
  dateLabel?: string;
  /** Day delta vs triage transaction date (client-side). */
  dateDeltaLabel?: string;
  /** Signed day count for coloring (option date − transaction date). */
  dateDeltaDays?: number | null;
  amountLabel?: string;
  /** Absolute delta vs triage transaction amount (client-side). */
  amountDeltaLabel?: string;
  /** Signed absolute delta (amount − |transaction|). */
  amountDelta?: number | null;
  contact?: {
    __typename: "ContactObject";
    id: string;
    identifier: string;
    name: string;
  } | null;
  /** Entity scope for contact pickers on document rows. */
  entityIdentifier?: string | null;
  /** Entity / bank-account breadcrumb crumbs (triage-row style). */
  breadcrumb?: Array<{
    nodeId?: string | null;
    label: string;
  }>;
  confidence?: number | null;
  categoryColor?: string | null;
  categories?: Array<{
    id: string;
    identifier: string;
    name: string;
    color?: string | null;
  }>;
  /**
   * A way to book it the business has not made yet, found for what is typed:
   * the catalogue's account (a ``new-ways`` stop), made as it is picked, then
   * picked as its own would be - or, on an account of its own, the category
   * its words name that it has not (``categoryName``), made under it as it is
   * booked.
   */
  way?: {
    catalogueKey: string | null;
    chartedAccountIdentifier: string | null;
    categoryName: string | null;
  };
  /** An account found by one of its categories' names: that one, filed under once it is picked. */
  foundCategory?: {
    id: string;
    identifier: string;
    name: string;
    color?: string | null;
  } | null;
  /** Changelist proposals: tag options on the suggestion. */
  tags?: Array<{
    identifier: string;
    name: string;
    color?: string | null;
  }>;
  /** Entity scope overline for changelist proposals (clickable like breadcrumbs). */
  entityScope?: {
    entity: { nodeId?: string | null; label: string };
    belongsTo?: { nodeId?: string | null; label: string };
  };
  /** Changelist proposals: linked document / transaction / JE indicators. */
  suggestionIndicatorSummary?: SuggestionIndicatorSummary;
  /** Raw suggestion for changelist proposal cards. */
  suggestion?: import("./TriageView.context").TriageSuggestionStub;
  /** Suggestions tab: rule/type headers own the title — card shows body only. */
  proposalGrouped?: boolean;
  /**
   * The ways to post it in another business that lend it there alike - this
   * one among them - each a choice on the one card they make.
   */
  alternatives?: TriageOptionListItem[];
  /**
   * A draft still in review in a chat that the payment would take - a receipt
   * forwarded to its business and read by the AI, say - with the chat it waits
   * in. Picked and booked, it is confirmed and booked at once.
   */
  draft?: Extract<
    NonNullable<TriageDraftMatchesQuery["node"]>,
    { draftMatches: unknown }
  >["draftMatches"][number];
  /**
   * The payment's business's forwarding address, to copy: a stop of the card
   * that searches the mailboxes for its receipt. Not yet known, a stop drawn
   * as it loads.
   */
  business?: {
    entityIdentifier: string;
    name: string;
    address: string | null;
  };
  /**
   * A document a search of the viewer's mailbox found for the payment: a stop of
   * the card of what was found. Enter opens it to preview, and import.
   */
  searchResult?: Extract<
    NonNullable<TriageEmailConnectionSearchResultsQuery["node"]>,
    { emailConnectionSearchResults: unknown }
  >["emailConnectionSearchResults"][number];
  /**
   * A file imported for the payment, being read in its chat as the server says
   * - the payment at work on it: drawn as one being read, opened again as it was.
   */
  isBeingRead?: boolean;
  /** A mailbox to search: a stop of the card that offers to. ``null``, the stop that connects one. */
  mailbox?: {
    identifier: string;
    emailAddress: string;
    provider: string | null;
    ownerName: string | null;
    ownedByViewer: boolean;
    /** Signed out by its provider: reconnected, not searched. */
    isSignedOut: boolean;
    /** Why its last search could not run - Nango unable to load it, its sign-in refused - until one does. */
    searchFailure: string | null;
  } | null;
  panelNodeId?: string | null;
  /**
   * The kind of suggestion its card is of - what the payment is matched to, a
   * copy of, moved as, posted to - and the list its search opens to pick from
   * by hand.
   */
  group?: {
    id: string;
    title: string;
    jobs: readonly string[];
    tab: TriageResolutionTab;
    /** What its search looks through, as its placeholder says: "documents". */
    searches: string;
  };
  /** Found for what is typed, on a card of its kind: a document, a payment, an account - or the way to more. */
  isFound?: boolean;
  /** A card of its kind looking for what is typed, until what it finds is in. */
  isSearching?: boolean;
  /** The words a card of its kind is searched for, said beside its title - or, on a stop, what found nothing. */
  searchedFor?: string[];
  /** The search for *searchedFor* could not run: why, as the server said - nothing was asked, so nothing was found. */
  searchFailure?: string;
  /** What *searchedFor* found nothing in is the mailboxes that could be searched, not every one. */
  someUnsearched?: boolean;
  /** On the Match card, the words its mailboxes are searched for, and whether that is still under way. */
  mailboxSearch?: { term: string; isSearching: boolean };
  /** A card of what a search found: its kind's name over it ("Match", "Post"). A suggestion's card has none. */
  isHeaded?: boolean;
  selected: boolean;
};

type TriageDocumentListFields = Pick<
  DocumentFragment,
  | "id"
  | "identifier"
  | "name"
  | "description"
  | "reference"
  | "amountTotal"
  | "amountDue"
  | "dateIssued"
  | "dateDue"
  | "createdAt"
  | "documentType"
  | "invoiceState"
  | "contact"
  | "entity"
>;

function startOfLocalDay(date: Date): number {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  ).getTime();
}

export function dateDeltaFromCompare(
  date: Date | null,
  compareDate?: Date | string | null,
): {
  dateLabel?: string;
  dateDeltaDays: number | null;
  dateDeltaLabel?: string;
} {
  const dateLabel = date ? Utils.fDate(date) : undefined;
  if (!date || compareDate == null) {
    return { dateLabel, dateDeltaDays: null };
  }
  const compare =
    compareDate instanceof Date ? compareDate : new Date(compareDate);
  if (Number.isNaN(compare.getTime()) || Number.isNaN(date.getTime())) {
    return { dateLabel, dateDeltaDays: null };
  }
  const dateDeltaDays = Math.round(
    (startOfLocalDay(date) - startOfLocalDay(compare)) / 86_400_000,
  );
  if (dateDeltaDays === 0) {
    return { dateLabel, dateDeltaDays: 0 };
  }
  const absDays = Math.abs(dateDeltaDays);
  return {
    dateLabel,
    dateDeltaDays,
    dateDeltaLabel: dateDeltaDays > 0 ? `+${absDays}d` : `−${absDays}d`,
  };
}

export function amountDeltaFromCompare(
  amount: number | null,
  compareAmount?: number | null,
): {
  amountLabel?: string;
  amountDelta: number | null;
  amountDeltaLabel?: string;
} {
  const amountLabel =
    amount != null ? Utils.fCurrency(Number(amount), false, true) : undefined;
  if (
    amount == null ||
    compareAmount == null ||
    !Number.isFinite(Number(compareAmount))
  ) {
    return { amountLabel, amountDelta: null };
  }
  const amountAbs = Math.abs(Number(amount));
  const transactionAbs = Math.abs(Number(compareAmount));
  const amountDelta = amountAbs - transactionAbs;
  if (Math.abs(amountDelta) < 0.005) {
    return {
      amountLabel,
      amountDelta: 0,
    };
  }
  const absFormatted = Utils.fCurrency(Math.abs(amountDelta), false, true);
  return {
    amountLabel,
    amountDelta,
    amountDeltaLabel: amountDelta > 0 ? `+${absFormatted}` : `−${absFormatted}`,
  };
}

function enumLabel(value: string | null | undefined) {
  if (!value) return null;
  return _.startCase(value.toString().toLowerCase());
}

function documentTypeEnumColor(
  type: DocumentEnum | null | undefined,
): (typeof EnumColors)[keyof typeof EnumColors] {
  if (type === DocumentEnum.Invoice || type === DocumentEnum.Bill) {
    return EnumColors.BLUE;
  }
  if (type === DocumentEnum.PurchaseOrder) {
    return EnumColors.AQUA;
  }
  if (type === DocumentEnum.CreditNote || type === DocumentEnum.DebitNote) {
    return EnumColors.ORANGE;
  }
  return EnumColors.GREY_1;
}

function invoiceStateEnumColor(
  state: InvoiceStateEnum | null | undefined,
  isOverdue?: boolean,
): (typeof EnumColors)[keyof typeof EnumColors] {
  if (state === InvoiceStateEnum.Paid) {
    return EnumColors.GREEN;
  }
  if (isOverdue) {
    return EnumColors.RED;
  }
  if (state === InvoiceStateEnum.Sent || state === InvoiceStateEnum.Ready) {
    return EnumColors.BLUE;
  }
  if (
    state === InvoiceStateEnum.Cancelled ||
    state === InvoiceStateEnum.Voided
  ) {
    return EnumColors.RED;
  }
  return EnumColors.GREY_1;
}

function confidenceEnumColor(
  confidence: number,
): (typeof EnumColors)[keyof typeof EnumColors] {
  if (confidence >= 0.9) return EnumColors.GREEN;
  if (confidence >= 0.7) return EnumColors.BLUE;
  return EnumColors.ORANGE;
}

export function documentToTriageListItem(
  document: TriageDocumentListFields,
  selectedDocumentIdentifiers: string[],
  compareAmount?: number | null,
  compareDate?: string | null,
): TriageOptionListItem {
  const contactName =
    document.contact?.organization?.name ||
    document.contact?.emailAddress ||
    null;
  const title =
    DocumentObject.getTitle(document as DocumentFragment) ?? "Document";
  const total = document.amountTotal ?? document.amountDue ?? null;

  const contact =
    document.contact?.id && document.contact?.identifier
      ? {
          __typename: "ContactObject" as const,
          id: document.contact.id,
          identifier: document.contact.identifier,
          name: contactName || "Contact",
        }
      : null;

  const entityName =
    document.entity?.name ??
    (document.entity?.identifier
      ? State.getState().entities.find(
          (entity) => entity.identifier === document.entity?.identifier,
        )?.name
      : null);
  const breadcrumb =
    entityName || document.entity?.id
      ? [
          {
            nodeId: document.entity?.id ?? null,
            label: entityName || "Entity",
          },
        ]
      : undefined;

  const dueDate = document.dateDue ? new Date(document.dateDue) : null;
  const isOverdue = Boolean(
    dueDate &&
    document.invoiceState !== InvoiceStateEnum.Paid &&
    dueDate.getTime() < Date.now(),
  );

  const dateFields = dateDeltaFromCompare(
    document.createdAt ? new Date(document.createdAt) : null,
    compareDate,
  );

  const amountFields = amountDeltaFromCompare(
    total != null ? Number(total) : null,
    compareAmount,
  );

  return {
    key: `document:${document.identifier}`,
    sectionId: "documents",
    selectionMode: "multi",
    title,
    documentType: document.documentType ?? null,
    invoiceState: document.invoiceState ?? null,
    isOverdue,
    contact,
    entityIdentifier: document.entity?.identifier ?? null,
    breadcrumb,
    ...dateFields,
    ...amountFields,
    panelNodeId: document.id,
    selected: selectedDocumentIdentifiers.includes(document.identifier),
  };
}

export function transactionToTriageListItem(
  transaction: {
    id: string;
    identifier: string;
    description?: string | null;
    amount?: number | string | null;
    effectiveDate?: string | null;
    chartedAccount?: { name?: string | null } | null;
    entity?: {
      id?: string | null;
      identifier?: string | null;
      name?: string | null;
    } | null;
    financialAccount?: {
      id?: string | null;
      name?: string | null;
    } | null;
  },
  selectedTransactionIdentifiers: string[],
  compareAmount?: number | null,
  compareDate?: string | null,
): TriageOptionListItem {
  const entityName =
    transaction.entity?.name ??
    (transaction.entity?.identifier
      ? State.getState().entities.find(
          (entity) => entity.identifier === transaction.entity?.identifier,
        )?.name
      : null);
  const bankAccountName = transaction.financialAccount?.name ?? null;
  const breadcrumb: TriageOptionListItem["breadcrumb"] = [];
  if (entityName || transaction.entity?.id) {
    breadcrumb.push({
      nodeId: transaction.entity?.id ?? null,
      label: entityName || "Entity",
    });
  }
  if (bankAccountName || transaction.financialAccount?.id) {
    breadcrumb.push({
      nodeId: transaction.financialAccount?.id ?? null,
      label: bankAccountName || "Bank account",
    });
  }

  const amount = transaction.amount != null ? Number(transaction.amount) : null;
  const amountFields = amountDeltaFromCompare(amount, compareAmount);
  const dateFields = dateDeltaFromCompare(
    transaction.effectiveDate ? new Date(transaction.effectiveDate) : null,
    compareDate,
  );

  return {
    key: `transaction:${transaction.identifier}`,
    sectionId: "transactions",
    selectionMode: "multi",
    title: transaction.description || "Untitled transaction",
    entityIdentifier: transaction.entity?.identifier ?? null,
    breadcrumb: breadcrumb.length > 0 ? breadcrumb : undefined,
    ...dateFields,
    ...amountFields,
    panelNodeId: transaction.id,
    selected: selectedTransactionIdentifiers.includes(transaction.identifier),
  };
}

function ConfidenceEnum({ confidence }: { confidence?: number | null }) {
  if (typeof confidence !== "number") return null;
  return (
    <SimpleEnum
      label={`${Math.round(Number(confidence) * 100)}%`}
      enumColor={confidenceEnumColor(Number(confidence))}
      size="sm"
    />
  );
}

function matchDateTrailing(
  item: TriageOptionListItem,
): SelectableListItemRowEnd | null {
  const delta =
    item.dateDeltaDays != null ? (
      <SelectableListItemRowDelta
        exact={item.dateDeltaDays === 0}
        label={item.dateDeltaLabel}
      />
    ) : null;
  const value = item.dateLabel ? (
    <SelectableListItemRowTitleEnd>
      {item.dateLabel}
    </SelectableListItemRowTitleEnd>
  ) : null;
  if (delta == null && value == null) {
    return null;
  }
  return { delta, value };
}

function matchMoneyTrailing(
  item: TriageOptionListItem,
): SelectableListItemRowEnd | null {
  const delta =
    item.amountDelta != null ? (
      <SelectableListItemRowDelta
        exact={item.amountDelta === 0}
        label={item.amountDeltaLabel}
      />
    ) : null;
  const value = item.amountLabel ? (
    <SelectableListItemRowTitleEndEmphasis>
      {item.amountLabel}
    </SelectableListItemRowTitleEndEmphasis>
  ) : null;
  if (delta == null && value == null) {
    return null;
  }
  return { delta, value };
}

function buildOptionChips(item: TriageOptionListItem): ReactNode | null {
  const typeLabel = enumLabel(item.documentType ?? undefined);
  const stateLabel = enumLabel(item.invoiceState ?? undefined);
  const categories = item.categories ?? [];
  const chips: ReactNode[] = [];

  if (item.confidence != null) {
    chips.push(
      <ConfidenceEnum key="confidence" confidence={item.confidence} />,
    );
  }
  if (typeLabel) {
    chips.push(
      <SimpleEnum
        key="document-type"
        label={typeLabel}
        enumColor={documentTypeEnumColor(item.documentType)}
        size="sm"
      />,
    );
  }
  if (stateLabel) {
    chips.push(
      <SimpleEnum
        key="invoice-state"
        label={stateLabel}
        enumColor={invoiceStateEnumColor(item.invoiceState, item.isOverdue)}
        size="sm"
      />,
    );
  }
  for (const category of categories) {
    chips.push(
      <SimpleEnum
        key={category.identifier}
        label={category.name}
        enumColor={categoryEnumColor(category.color)}
        size="sm"
        variant="outlined"
      />,
    );
  }

  if (chips.length === 0) {
    return null;
  }
  return <>{chips}</>;
}

export function OptionBreadcrumb({
  crumbs,
}: {
  crumbs: NonNullable<TriageOptionListItem["breadcrumb"]>;
}) {
  if (crumbs.length === 0) {
    return null;
  }
  return (
    <div
      data-testid="triage-option-breadcrumb"
      className="flex items-baseline min-w-0 flex-1 overflow-hidden shrink gap-1 font-medium text-xs leading-snug text-foreground"
      onClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
    >
      {crumbs.map((crumb, index) => (
        <div
          key={`${crumb.label}-${index}`}
          className={cn(
            "flex items-baseline min-w-0 overflow-hidden gap-1",
            index < crumbs.length - 1 ? "shrink-0" : "shrink",
          )}
        >
          {index > 0 ? (
            <Typography
              component="span"
              className="[font:inherit] inline align-baseline overflow-hidden text-ellipsis whitespace-nowrap text-muted-foreground/80 shrink-0"
            >
              /
            </Typography>
          ) : null}
          <div className="min-w-0 overflow-hidden">
            <TriageRowHeaderLink nodeId={crumb.nodeId}>
              {crumb.label}
            </TriageRowHeaderLink>
          </div>
        </div>
      ))}
    </div>
  );
}

function ChangelistOptionMeta({
  description,
  tags,
}: {
  description?: string;
  tags?: TriageOptionListItem["tags"];
}) {
  const tagList = tags ?? [];
  const hasTags = tagList.length > 0;
  const descriptionText = description?.trim() ?? "";

  if (!hasTags && !descriptionText) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex flex-col",
        hasTags && descriptionText ? "gap-1" : "gap-0",
        "min-w-0",
      )}
      onClick={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
    >
      {hasTags ? (
        <div className="flex flex-wrap items-center gap-1 min-w-0">
          {tagList.map((tag) => (
            <SimpleEnum
              key={tag.identifier}
              label={tag.name}
              enumColor={categoryEnumColor(tag.color)}
              size="sm"
            />
          ))}
        </div>
      ) : null}
      {descriptionText ? (
        <Typography
          level="body-sm"
          className="text-muted-foreground/80 whitespace-normal line-clamp-2"
        >
          {descriptionText}
        </Typography>
      ) : null}
    </div>
  );
}

export function buildTriageSuggestionGroups(
  triage: TriageDetail,
  selections: {
    chartedAccountIdentifier: string | null;
    documentIdentifiers: string[];
    duplicateOptionId: string | null;
    entityIdentifier: string | null;
  },
): SelectableCardListGroup<TriageOptionListItem>[] {
  const suggestions = (triage.suggestions ?? []) as TriageSuggestionStub[];
  const payerEntityId = triage.entity?.identifier;
  const settlementEntityIdentifier = selections.entityIdentifier;
  const settlementCrossEntityFilter =
    settlementEntityIdentifier != null &&
    payerEntityId != null &&
    settlementEntityIdentifier !== payerEntityId;
  const documentSuggestion = suggestions.find(
    (suggestion) => suggestion.job === "MATCH_DOCUMENT",
  );
  const duplicateSuggestion = suggestions.find(
    (suggestion) => suggestion.job === "DUPLICATE",
  );

  const groups: SelectableCardListGroup<TriageOptionListItem>[] = [
    {
      id: "documents",
      label: "Documents",
      selectionMode: "multi",
      items: (documentSuggestion?.ruleSuggestionDocumentOptions ?? []).flatMap(
        (option) => {
          const document = option.document;
          if (!document?.identifier) {
            return [];
          }
          if (
            settlementCrossEntityFilter &&
            document.entity?.identifier !== settlementEntityIdentifier
          ) {
            return [];
          }
          const paymentAmount = Math.abs(Number(triage.amount ?? 0));
          if (paymentAmount > 0) {
            const due = document.amountDue ?? document.amountTotal;
            const balanceDue =
              due != null && Number.isFinite(Number(due))
                ? Math.abs(Number(due))
                : 0;
            if (balanceDue <= 0 || paymentAmount > balanceDue + 0.005) {
              return [];
            }
          }
          return [
            {
              ...documentToTriageListItem(
                document,
                selections.documentIdentifiers,
                triage.amount != null ? Number(triage.amount) : null,
                triage.effectiveDate,
              ),
              confidence: documentSuggestion?.confidence,
            },
          ];
        },
      ),
    },
  ];

  if (duplicateSuggestion && !settlementCrossEntityFilter) {
    groups.push({
      id: "duplicates",
      label: "Duplicates",
      selectionMode: "single",
      items: (duplicateSuggestion.ruleSuggestionTransactionOptions ?? []).map(
        (option) => {
          const peer = option.transaction;
          return {
            key: `duplicate:${option.id}`,
            sectionId: "duplicates",
            selectionMode: "single",
            title: peer?.description || peer?.identifier || "Transaction",
            description: duplicateSuggestion.rationale ?? undefined,
            confidence: duplicateSuggestion.confidence,
            selected: selections.duplicateOptionId === option.id,
          };
        },
      ),
    });
  }

  return groups;
}

export function buildTriageSuggestionItems(
  triage: TriageDetail,
  selections: {
    chartedAccountIdentifier: string | null;
    documentIdentifiers: string[];
    duplicateOptionId: string | null;
    entityIdentifier: string | null;
  },
  groupFilter?: string[],
): TriageOptionListItem[] {
  return buildTriageSuggestionGroups(triage, selections)
    .filter((group) => (groupFilter ? groupFilter.includes(group.id) : true))
    .flatMap((group) => group.items);
}

export function firstOptionKeyForResolutionTab(
  tab: TriageResolutionTab,
  triage: TriageDetail,
  selections: {
    chartedAccountIdentifier: string | null;
    documentIdentifiers: string[];
    duplicateOptionId: string | null;
    entityIdentifier: string | null;
  },
  catalog: {
    chartedAccountsByIdentifier: Record<string, { identifier: string }>;
    documentsByIdentifier: Record<string, { identifier: string }>;
  },
): string | null {
  const recommendedKey = buildTriageSuggestionItems(
    triage,
    selections,
    TRIAGE_TAB_GROUP_FILTERS[tab],
  )[0]?.key;
  if (recommendedKey) {
    return recommendedKey;
  }

  if (tab === "document") {
    const document = Object.values(catalog.documentsByIdentifier)[0];
    return document?.identifier ? `document:${document.identifier}` : null;
  }

  if (tab === "categorize") {
    const account = Object.values(catalog.chartedAccountsByIdentifier)[0];
    return account?.identifier ? `account:${account.identifier}` : null;
  }

  return null;
}

export function TriageOptionSelectionControl({
  item,
  onToggle,
  disabled = false,
  variant = "outline",
  ...groupProps
}: {
  item: TriageOptionListItem;
  onToggle: () => void;
  /** Not to be picked as it stands - a draft, until it is accepted: unticked, out of reach. */
  disabled?: boolean;
  /** The group it sits in's: unticked, and booked, it is drawn as the buttons beside it are. */
  variant?: "outline" | "secondary";
} & Record<`data-${string}`, string | undefined>) {
  // The one that booked the payment says so in its tick's place: there is
  // nothing left to pick.
  if (item.suggestion?.status === TriageSuggestionStatusEnum.Accepted) {
    return (
      <Button
        {...groupProps}
        asChild
        variant={variant}
        size="xs"
        data-testid="triage-proposal-booked"
        tabIndex={-1}
        className="pl-1.5 pr-2 text-muted-foreground pointer-events-none"
      >
        <span>
          <Icon ReactIcon={CheckIcon} size="sm" />
          Booked
        </span>
      </Button>
    );
  }
  // A suggestion card is ticked in its corner by a button joined to its
  // Reject, its tick inside, though only one is taken at a time: picking
  // another unticks the first. Picked, solid; else an edge, its tick faint.
  if (item.proposalGrouped) {
    const isTicked = item.selected && !disabled;
    return (
      <Button
        // What the group it sits in marks it with - its first, its last - so
        // the group's ends are rounded.
        {...groupProps}
        variant={isTicked ? "default" : variant}
        size="icon-xs"
        disabled={disabled}
        // The tick it took the place of, said as one: ticked or not.
        role="checkbox"
        aria-checked={isTicked}
        aria-label={isTicked ? "Picked" : "Pick"}
        data-testid="triage-proposal-selection"
        tabIndex={-1}
        onClick={(event) => {
          event.stopPropagation();
          onToggle();
        }}
        // Keep focus on the list scope, as the tick it took the place of did.
        onMouseDown={(event) => event.preventDefault()}
        className={cn(
          "shrink-0",
          // Picked, solid; else its tick is faint at rest.
          !isTicked && "text-muted-foreground",
        )}
      >
        <Icon ReactIcon={CheckIcon} size="sm" />
      </Button>
    );
  }

  if (item.selectionMode === "multi") {
    return (
      <Checkbox
        checked={item.selected}
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.preventDefault()}
        onCheckedChange={() => onToggle()}
        tabIndex={-1}
      />
    );
  }

  return (
    // A group of one: stock's radio is an item of a group.
    <RadioGroup value={item.selected ? "picked" : ""} className="contents">
      <RadioGroupItem
        value="picked"
        aria-label={item.selected ? "Picked" : "Pick"}
        tabIndex={-1}
        onClick={(event) => {
          event.stopPropagation();
          onToggle();
        }}
        // Keep focus on the list scope so Space hits the list hotkey (toggle)
        // instead of native radio behavior (which cannot uncheck).
        onMouseDown={(event) => event.preventDefault()}
      />
    </RadioGroup>
  );
}

export function TriageOptionList({
  tab,
  items,
  loading = false,
  loadMore,
  emptyState,
  "data-testid": dataTestId = "triage-suggestions-options",
}: {
  /**
   * Whose rows these are. The suggestions draw their own inset and lines -
   * a row is a head that sticks over what it opens to - and are as tall as
   * what they say; a catalog's rows are one fixed height, and many.
   */
  tab: TriageResolutionTab;
  items?: TriageOptionListItem[];
  loading?: boolean;
  loadMore?: SelectableCardListLoadMore;
  emptyState?: {
    title: string;
    description?: string;
  };
  "data-testid"?: string;
}) {
  const {
    activeOptionKey,
    setActiveOptionKey,
    setSelection,
    selections,
    isSuggestionsKeyboardActive,
    registerCatalogDocuments,
    setPreviewingEmailConnectionSearchResult,
    setReconcilingManually,
    expandedOptionKeys,
    setOptionExpanded,
    triage,
    journalSettlement,
  } = useTriageDetailContext();
  const apolloClient = useApolloClient();
  const rowChromeless = tab === "suggestions";

  const selectOption = (key: string) => {
    setActiveOptionKey(key);
    commitActiveOption(key, setSelection);
  };

  const toggleDocumentOption = (item: TriageOptionListItem) => {
    const identifier = item.key.slice("document:".length);
    if (!identifier) return;
    setActiveOptionKey(item.key);
    setSelection("linesSuggestionIdentifier", null);
    setSelection("draftDocumentIdentifier", null);
    if (item.panelNodeId) {
      const cached = apolloClient.readFragment<DocumentFragment>({
        id: item.panelNodeId,
        fragment: DocumentFragmentDoc,
        fragmentName: "Document",
      });
      if (cached?.identifier) {
        registerCatalogDocuments([cached]);
      }
    }
    setSelection("documentIdentifiers", (current) =>
      current.includes(identifier)
        ? current.filter((id) => id !== identifier)
        : [...current, identifier],
    );
    setSelection("documentNodeIdsByIdentifier", (current) => {
      if (current[identifier]) {
        const { [identifier]: _removed, ...rest } = current;
        return rest;
      }
      if (!item.panelNodeId) {
        return current;
      }
      return { ...current, [identifier]: item.panelNodeId };
    });
  };

  const toggleTransactionOption = (item: TriageOptionListItem) => {
    const identifier = item.key.slice("transaction:".length);
    if (!identifier) return;
    setActiveOptionKey(item.key);
    setSelection("linesSuggestionIdentifier", null);
    setSelection("draftDocumentIdentifier", null);
    setSelection("transactionIdentifiers", (current) =>
      current.includes(identifier)
        ? current.filter((id) => id !== identifier)
        : [...current, identifier],
    );
  };

  const pickNoDocumentStop = useTriageNoDocumentStop();
  // A way to book it is made in the business its cost is in: where its Move
  // says it is for, not the one that paid it.
  const business =
    journalSettlement?.settlementEntityIdentifier ??
    triage.entity?.identifier ??
    null;
  const { mutate: makeWayToBook } = useBackendMutation({
    mutation: Mutations.MAKE_WAY_TO_BOOK,
  });

  // What a card found for what is typed does what it would in its own list:
  // a document or a payment picked, or let go; an account picked; a copy
  // opened to look at; the way to the rest opens the whole list. An account
  // not made yet is made as it is picked - with the category named on its
  // chip (*named*) to be made as it is booked, else the one it would make.
  const activateFound = (
    stop: TriageOptionListItem,
    tab: TriageResolutionTab,
    named?: string,
  ) => {
    if (stop.sectionId === "documents") {
      toggleDocumentOption(stop);
    } else if (stop.sectionId === "transactions") {
      toggleTransactionOption(stop);
    } else if (stop.sectionId === "accounts") {
      selectOption(stop.key);
      // Found by one of its categories: filed under that one, as it is
      // picked - or under one its words name that it has not, made as it is
      // booked.
      if (!stop.selected && stop.foundCategory) {
        setSelection("categoryIdentifier", stop.foundCategory.identifier);
      } else if (!stop.selected && stop.way?.categoryName) {
        setSelection("newCategoryName", stop.way.categoryName);
      }
    } else if (stop.sectionId === "new-ways" && stop.way && business) {
      // Made first - its account, its category under it - then picked as
      // the business's own: the account to post to, the category filed as.
      setActiveOptionKey(stop.key);
      void makeWayToBook({
        variables: {
          input: {
            entityIdentifier: business,
            ...stop.way,
            ...(named ? { categoryName: null } : {}),
          },
        },
        refetchQueries: ["TriageChartedAccountCatalog", "WaysToBook"],
        awaitRefetchQueries: true,
        callbacks: {
          onSuccess: (made) => {
            const account = (
              made as {
                chartedAccount?: { identifier: string } | null;
                category?: { identifier: string } | null;
              } | null
            )?.chartedAccount;
            if (!account) return;
            selectOption(`account:${account.identifier}`);
            const category = (
              made as { category?: { identifier: string } | null }
            )?.category;
            if (named) {
              setSelection("newCategoryName", named);
            } else if (category) {
              setSelection("categoryIdentifier", category.identifier);
            }
          },
        },
      });
    } else if (stop.sectionId === "duplicates") {
      setActiveOptionKey(stop.key);
      State.getState().pushToCommandStack({
        commandDefinitionId: "update-TransactionObject",
        context: { nodeId: stop.panelNodeId },
      });
    } else {
      setActiveOptionKey(stop.key);
      setReconcilingManually(tab);
    }
  };

  const activateOption = (item: TriageOptionListItem) => {
    // A draft in review is picked by Enter, as a suggestion is - though it is
    // not booked as it stands.
    const draft = item.group
      ? item.alternatives?.find(
          (one) => one.key === activeOptionKey && one.sectionId === "drafts",
        )
      : undefined;
    if (draft) {
      setActiveOptionKey(draft.key);
      commitActiveOption(draft.key, setSelection);
      return;
    }
    // One of what a card found for what is typed, as in its own list.
    const found = item.group
      ? item.alternatives?.find(
          (one) => one.key === activeOptionKey && one.isFound,
        )
      : undefined;
    if (item.group && found) {
      activateFound(found, item.group.tab);
      return;
    }
    // A way to its document in a mailbox, on the Match card, does what it is
    // for: what a search found opens to preview; a mailbox is searched, a
    // business's address copied.
    const mailboxStop = item.group
      ? item.alternatives?.find(
          (one) => one.key === activeOptionKey && !one.suggestion,
        )
      : undefined;
    if (mailboxStop) {
      if (mailboxStop.searchResult) {
        setActiveOptionKey(mailboxStop.key);
        setPreviewingEmailConnectionSearchResult(
          mailboxStop.searchResult.identifier,
        );
      } else {
        pickNoDocumentStop(mailboxStop);
      }
      return;
    }
    if (item.sectionId === "documents") {
      toggleDocumentOption(item);
      return;
    }
    if (item.sectionId === "transactions") {
      toggleTransactionOption(item);
      return;
    }
    if (item.sectionId === "lines") {
      // On a card of several ways, the one the keyboard is on.
      const way =
        item.alternatives?.find((stop) => stop.key === activeOptionKey) ?? item;
      setActiveOptionKey(way.key);
      // A card of its kind with nothing suggested: nothing to pick. Its list,
      // to pick from by hand, is in its kind's menu in the toolbar.
      if (item.group && !way.suggestion) {
        return;
      }
      // The one that booked the payment: nothing left to pick.
      if (way.suggestion?.status === TriageSuggestionStatusEnum.Accepted) {
        return;
      }
      if (way.suggestion) {
        toggleLinesSuggestion(
          way.suggestion,
          selections.linesSuggestionIdentifier,
          setSelection,
        );
      } else {
        commitActiveOption(item.key, setSelection);
      }
      return;
    }
    selectOption(item.key);
  };

  const handleRowClick = (item: TriageOptionListItem) => {
    // A card of its kind, clicked, is the keyboard's: on the stop it was on,
    // or its first. What a stop is for is done by a click on the stop, or
    // Enter.
    if (item.group) {
      const stops = item.alternatives ?? [item];
      if (!stops.some((stop) => stop.key === activeOptionKey) && stops[0]) {
        setActiveOptionKey(stops[0].key);
      }
      return;
    }
    activateOption(item);
  };

  const handleItemEnter = (
    item: TriageOptionListItem,
    _index: number,
    event: KeyboardEvent,
  ) => {
    event.preventDefault();
    activateOption(item);
  };

  const handleItemSpace = (
    item: TriageOptionListItem,
    _index: number,
    event: KeyboardEvent,
  ) => {
    event.preventDefault();
    // A suggestion or a draft on a card of its kind is looked at, as a Mac's
    // Space looks at a file - what its View opens; with none, what it opens
    // to under itself - and nothing is picked: Enter does that.
    const stop = item.group
      ? item.alternatives?.find((one) => one.key === activeOptionKey)
      : undefined;
    if (stop && (stop.suggestion || stop.sectionId === "drafts")) {
      const view = document.querySelector<HTMLElement>(
        '[data-testid="triage-proposal-view"]',
      );
      if (view) {
        view.click();
      } else if (
        document.querySelector(
          `[data-option-key="${CSS.escape(stop.key)}"][data-expandable]`,
        )
      ) {
        setOptionExpanded?.(
          stop.key,
          !(expandedOptionKeys ?? []).includes(stop.key),
        );
      }
      return;
    }
    activateOption(item);
  };

  return (
    <SelectableCardList
      items={items}
      loading={loading}
      loadingItemCount={TRIAGE_OPTION_LIST_SKELETON_COUNT}
      // Loading, the rows are a typical row of this tab's, drawn as any is.
      placeholderItem={TRIAGE_OPTION_PLACEHOLDERS[tab]}
      loadMore={loadMore}
      size="md"
      // The one the person is on is a card, lifted off the list's ground - a
      // suggestion's, and a catalog row's as the queue's is: a tint a step
      // off the ground it sits on read as nothing there.
      variant="outlined"
      layout="cards"
      rowChromeless={rowChromeless}
      // Only being chosen makes a row a card, as in the queue: under a moving
      // pointer it is the title that answers.
      rowHover="title"
      // And the chosen suggestion stays in sight as the list scrolls, as the
      // chosen payment does in the queue.
      stickyActiveRow={rowChromeless}
      emptyState={
        emptyState
          ? {
              title: emptyState.title,
              description: emptyState.description,
            }
          : undefined
      }
      estimatedItemHeight={OPTION_ROW_HEIGHTS[tab]}
      variableItemHeight
      getItemKey={(item) => item.key}
      // A card of several ways to post it stops the keyboard on each, and is
      // the one the keyboard is on while it is on any of them.
      getItemStops={(item) =>
        (item.alternatives ?? [item]).map((stop) => stop.key)
      }
      isItemActive={(item) =>
        (item.alternatives ?? [item]).some(
          (stop) => stop.key === activeOptionKey,
        )
      }
      onItemClick={handleRowClick}
      onItemNavigate={(_item, _index, stop) => setActiveOptionKey(stop)}
      onItemEnter={handleItemEnter}
      onItemSpace={handleItemSpace}
      scrollToActiveKey={activeOptionKey}
      disableVirtualization={rowChromeless}
      data-testid={dataTestId}
      keyboardScope="global"
      isKeyboardActive={isSuggestionsKeyboardActive}
      // In the panel's search, the keyboard is the suggestions' still: the
      // card it steps to is drawn as the one it is on.
      keyboardSourceRef={rowChromeless ? SUGGESTIONS_SEARCH : undefined}
      // Its inset inside its width: as wide as the panel, so the panel's edge
      // cuts off none of a card or the keyboard's ring round it.
      className="w-full box-border"
      renderItem={({ item, isActive, isLoading }) => {
        const group = item.group;
        if (group) {
          return (
            <TriageSuggestionCard
              item={item}
              isActive={isActive}
              isLoading={isLoading}
              onActivateFound={(stop, named) =>
                activateFound(stop, group.tab, named)
              }
            />
          );
        }
        const isDocument = item.sectionId === "documents";
        const isTransaction = item.sectionId === "transactions";
        const isAccount = item.sectionId === "accounts";
        const isLines = item.sectionId === "lines";
        const isMatchMoneyRow = isDocument || isTransaction;
        const isCatalog = isDocument || isTransaction || isAccount;
        const dateTrailing = isMatchMoneyRow ? matchDateTrailing(item) : null;
        const moneyTrailing = isMatchMoneyRow ? matchMoneyTrailing(item) : null;
        const optionChips = buildOptionChips(item);
        const breadcrumbCrumbs: NonNullable<
          TriageOptionListItem["breadcrumb"]
        > = [
          ...(item.breadcrumb ?? []),
          // A document's contact after its business: who it is from.
          ...(isDocument && item.contact
            ? [{ nodeId: item.contact.id, label: item.contact.name }]
            : []),
          ...(item.entityScope
            ? [
                ...(item.entityScope.belongsTo
                  ? [item.entityScope.belongsTo]
                  : []),
                item.entityScope.entity,
              ]
            : []),
        ];
        const hasCrumbs = breadcrumbCrumbs.length > 0;
        // A catalog's row is what it says, and as tall: an account its path in
        // the chart and its categories under it; a document its business and
        // contact over its title and amount, and its kind and state under its
        // title; a payment its business and bank account over its title and
        // amount.
        return (
          <SelectableListItemRow
            overline={
              hasCrumbs ? (
                <OptionBreadcrumb crumbs={breadcrumbCrumbs} />
              ) : undefined
            }
            overlineEnd={dateTrailing ?? undefined}
            title={item.title}
            description={
              isLines ? (
                (item.tags?.length ?? 0) > 0 ||
                Boolean(item.description?.trim()) ? (
                  <ChangelistOptionMeta
                    description={item.description}
                    tags={item.tags}
                  />
                ) : undefined
              ) : isDocument || isAccount ? (
                optionChips ? (
                  <div
                    data-testid="triage-option-chips"
                    // As far under the title as the queue's row and a
                    // suggestion's card set their chips.
                    className="flex flex-wrap gap-1 mt-2.5"
                  >
                    {optionChips}
                  </div>
                ) : undefined
              ) : isCatalog ? undefined : (
                item.description
              )
            }
            leading={
              // Level with its first line: the name, when nothing is over it.
              <div
                className={cn(
                  "flex items-center",
                  hasCrumbs ? "" : "min-h-5.5",
                )}
              >
                <TriageOptionSelectionControl
                  item={item}
                  onToggle={() => activateOption(item)}
                />
              </div>
            }
            titleEnd={
              moneyTrailing ??
              (item.amountLabel ? (
                <SelectableListItemRowTitleEndEmphasis>
                  {item.amountLabel}
                </SelectableListItemRowTitleEndEmphasis>
              ) : undefined)
            }
            descriptionEnd={
              isMatchMoneyRow ? undefined : item.dateLabel ? (
                <SelectableListItemRowTitleEnd>
                  {item.dateLabel}
                </SelectableListItemRowTitleEnd>
              ) : undefined
            }
            footerEnd={
              isLines ? (
                item.suggestionIndicatorSummary || optionChips ? (
                  <>
                    {item.suggestionIndicatorSummary ? (
                      <TriageSuggestionIndicatorPill
                        data-testid="triage-suggestion-card-indicator"
                        summary={item.suggestionIndicatorSummary}
                      />
                    ) : null}
                    {optionChips}
                  </>
                ) : undefined
              ) : isCatalog ? undefined : (
                (optionChips ?? undefined)
              )
            }
            emphasized={item.selected}
            isLoading={isLoading}
          />
        );
      }}
    />
  );
}
